import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Col } from "react-bootstrap";
import { IoCloseSharp, IoFilter } from "react-icons/io5";
import { MdFilterAltOff } from "react-icons/md";
import { FaTimes } from "react-icons/fa";
import DatePicker, { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import { pdf } from "@react-pdf/renderer";
import RelatorioPDF from "./RelatorioPDF";

import {
  Container,
  ContainerButton,
  LoadingContainer,
  AppliedFiltersWrapper,
  FilterTitle,
  FiltersContainer,
  FilterItem,
  FilterKey,
  FilterValue,
} from "./styles";
import {
  BlueTitle,
  ButtonGroupEnd,
  GrayTitle,
  LoadingGif,
  LoadingOverlay,
  ModalBody,
  ModalBodyContainer,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  StyledHR,
  TextError,
  TitleAndButton,
} from "../../../shared/styles/global.styles";
import loadSCGif from "../../../assets/load_SC.gif";
import {
  Table,
  TableContainer,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/Table/styles";
import Button from "../../../components/Button/button";
import { Label } from "../../../components/Label/styles";
import SelectComponent from "../../../components/Select/select";
import { DatePickerInputContainer } from "../../../components/Input/styles";
import { Lotacao } from "../../../shared/interfaces/DadosFinanceirosInterface";
import lotacaoService from "../../../services/LotacaoService";
import RelatorioService from "../../../services/RelatorioService";
import localPagamentoService from "../../../services/LocalPagamentoService";
import { LocalPagamentoDto } from "../../../shared/dtos/LocalPagamento.dto";

// Registrar o locale pt-BR
registerLocale("pt-BR", ptBR);

// Interface para os dados de ficha financeira no relatório
interface BoletoPagoCaixaItem {
  nomeContribuinte: string;
  cpfContribuinte: string;
  dataEmissao: string;
  dataVencimento: string;
  dataPagamento: string;
  valorParcela: string;
  valorParcelaNumerico: number;
  idContribuinte: number;
  idFichaFinanceira: number;
  localPagamento: string;
}

// Interface para a resposta do relatório
interface RelatorioResponse {
  titulo: string;
  periodo: string;
  dataGeracao: string;
  fichasFinanceiras: BoletoPagoCaixaItem[];
  totalRegistros: number;
  valorTotal: number;
  exemploRegistro?: BoletoPagoCaixaItem;
}

const RelatorioBoletosPagosCaixa: React.FC = () => {
  const [lotacoes, setLotacoes] = useState<Lotacao[]>([]);
  const [locaisPagamento, setLocaisPagamento] = useState<LocalPagamentoDto[]>(
    []
  );
  const [selectLotacao, setSelectLotacao] = useState<Lotacao>();
  const [selectLocalPagamento, setSelectLocalPagamento] =
    useState<LocalPagamentoDto>();
  const [dateRange, setDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined]);
  const [appliedLotacao, setAppliedLotacao] = useState<Lotacao>();
  const [appliedLocalPagamento, setAppliedLocalPagamento] =
    useState<LocalPagamentoDto>();
  const [appliedDateRange, setAppliedDateRange] = useState<
    [Date | undefined, Date | undefined]
  >([undefined, undefined]);
  const [showModalReport, setShowModalReport] = useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRequest, setIsRequest] = useState<boolean>(false);

  const [relatorioData, setRelatorioData] = useState<RelatorioResponse | null>(
    null
  );
  const [relatorioBoletosPagos, setRelatorioBoletosPagos] = useState<
    BoletoPagoCaixaItem[]
  >([]);
  const [PDF, setPDF] = useState<Blob>();
  const clearError = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name } = event.target;
    setInvalidFields((prev) => prev.filter((field) => field !== name));
  };

  const fetchLotacoes = async () => {
    try {
      const response = await lotacaoService.getAllLotacoes();
      setLotacoes(response);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar lotações. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        allowOutsideClick: false,
      });
    }
  };

  const fetchLocaisPagamento = async () => {
    try {
      const response = await localPagamentoService.getAllLocaisPagamento();
      setLocaisPagamento(response);
    } catch (error) {
      Swal.fire({
        title: "Erro",
        text: "Erro ao buscar locais de pagamento. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        allowOutsideClick: false,
      });
    }
  };

  const resetForm = () => {
    setSelectLotacao(undefined);
    setSelectLocalPagamento(undefined);
    setDateRange([undefined, undefined]);
    setInvalidFields([]);
  };

  const closeReportModal = () => {
    setShowModalReport(false);
    resetForm();
    setIsRequest(false);
    setIsLoading(false);
  };

  const generateReport = async () => {
    setIsRequest(true);
    setIsLoading(true);
    const newInvalidFields: string[] = [];
    if ((!dateRange[0] && !dateRange[1]) || !dateRange[0] || !dateRange[1]) {
      newInvalidFields.push("dataVencimento");
    }

    // Verificar se a data final é menor que a data inicial (período inválido)
    if (dateRange[0] && dateRange[1] && dateRange[1] < dateRange[0]) {
      newInvalidFields.push("dataVencimento");

      Swal.fire({
        icon: "error",
        title: "Período inválido",
        text: "A data final não pode ser menor que a data inicial.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });

      setInvalidFields(newInvalidFields);
      setIsRequest(false);
      setIsLoading(false);
      return;
    }

    // Verificar se o período é muito longo
    if (dateRange[0] && dateRange[1]) {
      const diffTime = Math.abs(
        dateRange[1].getTime() - dateRange[0].getTime()
      );
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Se período for maior que 31 dias (aproximadamente 1 mês), exibir alerta
      if (diffDays > 31) {
        const result = await Swal.fire({
          title: "Atenção",
          text: "O período selecionado é longo e pode causar lentidão. Deseja continuar mesmo assim?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Sim, continuar",
          cancelButtonText: "Não, ajustar período",
          allowOutsideClick: false,
        });

        if (!result.isConfirmed) {
          setIsRequest(false);
          setIsLoading(false);
          return;
        }
      }
    }

    if (newInvalidFields.length > 0) {
      setInvalidFields(newInvalidFields);

      Swal.fire({
        icon: "error",
        title: "Erro nos campos",
        text: "Por favor, preencha todos os campos obrigatórios.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
      });

      setIsRequest(false);
      setIsLoading(false);
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Confirmar",
      text: "Você tem certeza que deseja gerar o relatório de boletos pagos no caixa?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Cancelar",
      allowOutsideClick: false,
    });

    if (!confirmResult.isConfirmed) {
      setIsRequest(false);
      setIsLoading(false);
      return;
    }

    Swal.fire({
      title: "Gerando relatório...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      if (dateRange[0] && dateRange[1]) {
        const result = await RelatorioService.getBoletosPagosCaixaReport(
          dateRange[0],
          dateRange[1],
          selectLotacao?.id?.toString() ?? undefined,
          selectLocalPagamento?.id?.toString() ?? undefined
        );

        if (result && typeof result === "object") {
          // Armazenar o relatório completo
          setRelatorioData(result as RelatorioResponse);

          // Extrair as fichas financeiras para exibição na tabela
          let fichasParaExibir: BoletoPagoCaixaItem[] = [];

          if (
            result.fichasFinanceiras &&
            Array.isArray(result.fichasFinanceiras) &&
            result.fichasFinanceiras.length > 0
          ) {
            fichasParaExibir = result.fichasFinanceiras;
          } else if (result.exemploRegistro && result.totalRegistros > 0) {
            // Se não tiver fichasFinanceiras mas tiver um exemploRegistro, criar uma lista
            // baseada no exemploRegistro
            fichasParaExibir = Array(result.totalRegistros)
              .fill(null)
              .map((_, i) => {
                return { ...result.exemploRegistro! };
              });
          }

          setRelatorioBoletosPagos(fichasParaExibir);

          // Guardar os filtros aplicados
          setAppliedLotacao(selectLotacao);
          setAppliedLocalPagamento(selectLocalPagamento);
          setAppliedDateRange(dateRange);
        } else {
          setRelatorioData(null);
          setRelatorioBoletosPagos([]);
        }

        // Fecha o modal após obter os resultados com sucesso
        setShowModalReport(false);
        // Limpar o formulário para próxima pesquisa
        resetForm();

        if (
          !result ||
          ((!result.fichasFinanceiras ||
            !Array.isArray(result.fichasFinanceiras) ||
            result.fichasFinanceiras.length === 0) &&
            (!result.exemploRegistro || !result.totalRegistros))
        ) {
          Swal.fire({
            icon: "info",
            title: "Informação",
            text: "Não foram encontrados boletos pagos no caixa para o período selecionado.",
            allowOutsideClick: false,
          });
          // Se for apenas um erro de "sem dados", limpar os dados anteriores
          if (result && !result.fichasFinanceiras && !result.exemploRegistro) {
            setRelatorioData(null);
            setRelatorioBoletosPagos([]);
            // Ainda fecha o modal em caso de "sem dados"
            setShowModalReport(false);
            // Limpar o formulário para próxima pesquisa
            resetForm();
          }
        } else {
          // Preparar os dados para possível impressão
          const dadosFinais =
            result.fichasFinanceiras &&
            Array.isArray(result.fichasFinanceiras) &&
            result.fichasFinanceiras.length > 0
              ? result.fichasFinanceiras
              : result.exemploRegistro && result.totalRegistros > 0
              ? Array(result.totalRegistros)
                  .fill(null)
                  .map(() => ({ ...result.exemploRegistro! }))
              : [];

          // Garantir que estamos usando as datas corretas
          // Importante: Atualizar o estado antes de chamar qualquer função que use esses valores
          const dataInicio = dateRange[0];
          const dataFim = dateRange[1];

          // Atualizar o estado com os dados finais e guardar as datas aplicadas
          setRelatorioBoletosPagos(dadosFinais);
          setAppliedDateRange([dataInicio, dataFim]);
          setAppliedLotacao(selectLotacao);
          setAppliedLocalPagamento(selectLocalPagamento);

          // Limpar o formulário para próxima pesquisa
          resetForm();

          // Mostrar o modal de sucesso
          Swal.fire({
            icon: "success",
            title: "Sucesso",
            text: "Relatório gerado com sucesso!",
            showCancelButton: true,
            confirmButtonText: "Imprimir",
            cancelButtonText: "Fechar",
            allowOutsideClick: false,
          }).then((result) => {
            if (result.isConfirmed) {
              // Imprimir PDF diretamente usando os dados já preparados e as datas aplicadas
              gerarEImprimirPDFDireto(dadosFinais, dataInicio, dataFim);
            }
          });
        }
      } else {
        throw new Error("Datas inválidas para gerar o relatório.");
      }
    } catch (error: any) {
      console.error("Erro ao gerar relatório:", error);

      // Determinar se é um erro de "sem dados" ou outro tipo de erro
      const errorMessage = error.message || "";
      const isSemDadosError =
        errorMessage.includes("Não foram encontrados boletos") ||
        errorMessage.toLowerCase().includes("sem registro") ||
        errorMessage.toLowerCase().includes("nenhum boleto") ||
        errorMessage.toLowerCase().includes("não existem boletos") ||
        errorMessage === "Resposta vazia do servidor";

      Swal.fire({
        icon: "info",
        title: "Informação",
        text: isSemDadosError
          ? "Não foram encontrados boletos pagos no caixa para o período selecionado."
          : errorMessage ||
            "Ocorreu um erro ao gerar o relatório. Por favor, tente novamente ou escolha um período menor.",
        allowOutsideClick: false,
      });

      // Se for apenas um erro de "sem dados", limpar os dados anteriores
      if (isSemDadosError) {
        setRelatorioData(null);
        setRelatorioBoletosPagos([]);
        // Ainda fecha o modal em caso de "sem dados"
        setShowModalReport(false);
        // Limpar o formulário para próxima pesquisa
        resetForm();
      }
    }

    setIsRequest(false);
    setIsLoading(false);
  };

  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    // Convertendo valores null para undefined
    const [start, end] = dates;

    setDateRange([start || undefined, end || undefined]);
  };

  // Função para formatar a entrada manual de datas
  const handleDateInputChange = (e: any) => {
    if (!e || !e.target || typeof e.target.value !== "string") return;

    let value = e.target.value;

    // Não fazer nada se o campo estiver vazio
    if (!value) return;

    // Verificar se o valor contém hífen (separando intervalo de datas)
    const parts = value.split("-");

    if (parts.length > 1) {
      // Processar intervalo de datas (início - fim)
      let startDate = parts[0].trim();
      let endDate = parts[1].trim();

      // Formatar a primeira data
      startDate = formatDateInput(startDate);

      // Formatar a segunda data
      endDate = formatDateInput(endDate);

      // Recompor o valor com o formato correto
      e.target.value = `${startDate} - ${endDate}`;
    } else {
      // Processar uma única data
      const formattedDate = formatDateInput(value);

      // Verificar se a primeira data está completa (10 caracteres = dd/mm/aaaa)
      // Se estiver completa e não for uma operação de apagar (verificar se o valor atual é maior que o anterior)
      if (formattedDate.length === 10 && value.length >= formattedDate.length) {
        // Adicionar o hífen automaticamente
        e.target.value = `${formattedDate} - `;
      } else {
        e.target.value = formattedDate;
      }
    }
  };

  // Função auxiliar para formatar uma data individual
  const formatDateInput = (value: string): string => {
    // Remover caracteres não numéricos
    let numbers = value.replace(/\D/g, "");

    // Limitar a 8 dígitos (ddmmyyyy)
    numbers = numbers.substring(0, 8);

    // Aplicar máscara dd/mm/yyyy
    let formattedValue = "";
    if (numbers.length > 0) {
      formattedValue = numbers.substring(0, Math.min(2, numbers.length));

      if (numbers.length > 2) {
        formattedValue +=
          "/" + numbers.substring(2, Math.min(4, numbers.length));
      }

      if (numbers.length > 4) {
        formattedValue +=
          "/" + numbers.substring(4, Math.min(8, numbers.length));
      }
    }

    return formattedValue;
  };

  // Formatar valor monetário
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Formatar data
  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === "-") return "-";

    // Tentar formato brasileiro dd/mm/yyyy
    if (dateString.includes("/")) {
      return dateString; // Já está formatado corretamente
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Retorna a string original se for inválida

      return new Intl.DateTimeFormat("pt-BR").format(date);
    } catch (error) {
      return dateString; // Retorna a string original em caso de erro
    }
  };

  // Função específica para gerar e imprimir o PDF, sem depender do estado do React
  const gerarEImprimirPDF = async (dados: BoletoPagoCaixaItem[]) => {
    try {
      if (!dados || dados.length === 0) {
        console.error("Tentativa de imprimir com dados vazios");
        Swal.fire({
          icon: "info",
          title: "Informação",
          text: "Não há dados para imprimir. Por favor, gere o relatório novamente.",
          allowOutsideClick: false,
        });
        return;
      }

      // Garantir que estamos usando as datas corretas para o PDF
      // Se não tivermos as datas aplicadas, usar as selecionadas no momento da geração
      const dataInicio = appliedDateRange[0] || dateRange[0];
      const dataFim = appliedDateRange[1] || dateRange[1];

      if (!dataInicio || !dataFim) {
        console.warn("Datas de início ou fim estão faltando");
      }
      const blob = await pdf(
        <RelatorioPDF
          data={dados}
          lotacao={appliedLotacao?.descricao}
          localPagamento={appliedLocalPagamento?.descricao}
          dateRange={{
            inicio: dataInicio?.toLocaleDateString("pt-BR") || "",
            fim: dataFim?.toLocaleDateString("pt-BR") || "",
          }}
          totalRegistros={relatorioData?.totalRegistros || dados.length}
          valorTotal={relatorioData?.valorTotal || 0}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      window.open(url);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Ocorreu um erro ao gerar o PDF para impressão.",
        allowOutsideClick: false,
      });
    }
  };

  // Função para imprimir diretamente do modal de sucesso, sem depender do estado React
  const gerarEImprimirPDFDireto = async (
    dados: BoletoPagoCaixaItem[],
    dataInicio?: Date,
    dataFim?: Date
  ) => {
    try {
      if (!dados || dados.length === 0) {
        console.error("Tentativa de imprimir com dados vazios");
        Swal.fire({
          icon: "info",
          title: "Informação",
          text: "Não há dados para imprimir. Por favor, gere o relatório novamente.",
          allowOutsideClick: false,
        });
        return;
      }

      // Verificação explícita para garantir que temos as datas
      if (!dataInicio || !dataFim) {
        console.warn(
          "Datas de início ou fim estão faltando no gerarEImprimirPDFDireto"
        );
      }
      Swal.fire({
        title: "Gerando PDF do relatório...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      await new Promise((resolve) => setTimeout(resolve, 1000));

      if (PDF) {
        const url = URL.createObjectURL(PDF);
        window.open(url);
        Swal.close();
        return;
      }
      console.log(dados.length);
      console.log(selectLotacao?.descricao);
      console.log(selectLocalPagamento?.descricao);
      console.log(dateRange);
      console.log(relatorioData?.valorTotal);
      await pdf(
        <RelatorioPDF
          data={dados}
          lotacao={selectLotacao?.descricao}
          localPagamento={selectLocalPagamento?.descricao}
          dateRange={{
            inicio: dataInicio?.toLocaleDateString("pt-BR") || "",
            fim: dataFim?.toLocaleDateString("pt-BR") || "",
          }}
          totalRegistros={relatorioData?.totalRegistros || dados.length}
          valorTotal={relatorioData?.valorTotal || 0}
        />
      )
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url);
          setPDF(blob);
        })
        .finally(() => {
          Swal.close();
          closeReportModal();
        });
    } catch (error) {
      console.error("Erro ao gerar PDF direto:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Ocorreu um erro ao gerar o PDF para impressão.",
        allowOutsideClick: false,
      });
    }
  };

  // Função para imprimir o relatório a partir do botão na interface
  const imprimirRelatorio = async () => {
    if (!relatorioData || !relatorioBoletosPagos.length) {
      Swal.fire({
        icon: "info",
        title: "Informação",
        text: "Não há dados para imprimir. Por favor, gere o relatório primeiro.",
        allowOutsideClick: false,
      });
      return;
    }

    gerarEImprimirPDF(relatorioBoletosPagos);
  };

  // Função para limpar os resultados do relatório
  const limparResultados = () => {
    // Limpar os dados do relatório
    setRelatorioData(null);
    setRelatorioBoletosPagos([]);

    // Limpar os filtros aplicados
    setAppliedLotacao(undefined);
    setAppliedLocalPagamento(undefined);
    setAppliedDateRange([undefined, undefined]);
    setPDF(undefined);
  };

  // Funções para remover filtros individuais
  const removerFiltroLotacao = () => {
    setAppliedLotacao(undefined);

    // Se ainda houver outros filtros, refazer a busca
    if (appliedLocalPagamento || (appliedDateRange[0] && appliedDateRange[1])) {
    } else {
      // Se não houver mais filtros, limpar todos os resultados
      limparResultados();
    }
  };

  const removerFiltroLocalPagamento = () => {
    setAppliedLocalPagamento(undefined);

    // Se não houver mais filtros, limpar todos os resultados
    if (!appliedLotacao || (!appliedDateRange[0] && !appliedDateRange[1])) {
      limparResultados();
    }
  };

  const removerFiltroPeriodo = () => {
    setAppliedDateRange([undefined, undefined]);

    // Se ainda houver outros filtros, refazer a busca
    if (!appliedLotacao || !appliedLocalPagamento) {
      limparResultados();
    }
  };

  useEffect(() => {
    fetchLotacoes();
    fetchLocaisPagamento();
  }, []);

  return (
    <>
      {!isLoading ? (
        <Container>
          <TitleAndButton>
            <GrayTitle>
              Relatório de boletos enviados por Remessa e pagos no Caixa
            </GrayTitle>
            <ContainerButton>
              {relatorioBoletosPagos.length > 0 && (
                <>
                  <Button
                    schemaButton="out-primary"
                    action={() => imprimirRelatorio()}
                    disabled={isRequest}
                  >
                    Imprimir
                  </Button>
                  <Button
                    schemaButton="full-primary"
                    action={limparResultados}
                    disabled={isRequest}
                  >
                    Limpar resultados
                  </Button>
                </>
              )}
              {relatorioBoletosPagos.length <= 0 && (
                <Button
                  schemaButton="full-primary"
                  action={() => setShowModalReport(true)}
                  disabled={isRequest}
                >
                  Gerar Relatório
                </Button>
              )}
            </ContainerButton>
          </TitleAndButton>
          <StyledHR />

          {(relatorioBoletosPagos.length > 0 || relatorioData) && (
            <AppliedFiltersWrapper>
              <FilterTitle>Dados do relatório:</FilterTitle>
              <FiltersContainer>
                {appliedLotacao && (
                  <FilterItem>
                    <FilterKey>Lotação:</FilterKey>
                    <FilterValue>{appliedLotacao.descricao}</FilterValue>
                  </FilterItem>
                )}
                {appliedLocalPagamento && (
                  <FilterItem>
                    <FilterKey>Local de Pagamento:</FilterKey>
                    <FilterValue>{appliedLocalPagamento.descricao}</FilterValue>
                  </FilterItem>
                )}
                {appliedDateRange[0] && appliedDateRange[1] && (
                  <FilterItem>
                    <FilterKey>Período:</FilterKey>
                    <FilterValue>
                      {appliedDateRange[0].toLocaleDateString("pt-BR")} até{" "}
                      {appliedDateRange[1].toLocaleDateString("pt-BR")}
                    </FilterValue>
                  </FilterItem>
                )}
              </FiltersContainer>
            </AppliedFiltersWrapper>
          )}

          <TableContainer>
            {isRequest ? (
              <LoadingContainer>
                <LoadingGif src={loadSCGif} alt="Carregando..." />
              </LoadingContainer>
            ) : (
              <Table
                id="relatorio-boletos-pagos-tabela"
                style={{ minHeight: "175px" }}
              >
                <TableHead>
                  <TableRow>
                    <TableHeader>Nome Contribuinte</TableHeader>
                    <TableHeader>CPF</TableHeader>
                    <TableHeader>Data de Emissão</TableHeader>
                    <TableHeader>Data de Vencimento</TableHeader>
                    <TableHeader>Data de Pagamento</TableHeader>
                    <TableHeader>Valor Parcela</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {relatorioBoletosPagos.length > 0 ? (
                    <>
                      {relatorioBoletosPagos.map((item, index) => (
                        <TableRow key={index}>
                          <TableData>{item.nomeContribuinte}</TableData>
                          <TableData>{item.cpfContribuinte}</TableData>
                          <TableData>{formatDate(item.dataEmissao)}</TableData>
                          <TableData>
                            {formatDate(item.dataVencimento)}
                          </TableData>
                          <TableData>
                            {formatDate(item.dataPagamento)}
                          </TableData>
                          <TableData>
                            {item.valorParcela?.includes("R$")
                              ? item.valorParcela
                              : item.valorParcela
                              ? `R$ ${item.valorParcela}`
                              : formatCurrency(item.valorParcelaNumerico)}
                          </TableData>
                        </TableRow>
                      ))}

                      {relatorioData && relatorioData.totalRegistros > 0 && (
                        <TableRow className="total-row">
                          <TableData colSpan={5}>
                            TOTAL ({relatorioData.totalRegistros} registro(s))
                          </TableData>
                          <TableData>
                            {formatCurrency(relatorioData.valorTotal)}
                          </TableData>
                        </TableRow>
                      )}
                    </>
                  ) : (
                    <TableRow>
                      <TableData colSpan={6} className="text-center text-muted">
                        {"Sem resultados"}
                      </TableData>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            )}
          </TableContainer>

          <ModalContainer show={showModalReport}>
            <ModalContent style={{ marginTop: "2%" }} width={35}>
              <Button
                type="button"
                schemaButton="x-close"
                action={() => closeReportModal()}
                icon={<IoCloseSharp size={30} />}
                position="absolute"
                top={10}
                right={10}
              />
              <ModalHeader>
                <BlueTitle>Gerar relatório</BlueTitle>
              </ModalHeader>
              <ModalBody>
                <Col>
                  <Label htmlFor="lotacao">Lotação</Label>
                  <SelectComponent
                    color="black"
                    placeholder="Todas lotações"
                    isPlaceHolder={false}
                    id="lotacao"
                    width={Number("100%")}
                    name="lotacao"
                    disabled={isRequest}
                    value={selectLotacao?.id?.toString() ?? ""}
                    onChange={(e) => {
                      if (e.target.value === "0") {
                        setSelectLotacao({ id: 0, descricao: "Sem lotação" });
                      } else {
                        const selectedLotacao = lotacoes.find(
                          (lotacao) => lotacao.id === parseInt(e.target.value)
                        );
                        setSelectLotacao(selectedLotacao);
                      }
                      clearError(e);
                    }}
                    options={[
                      { value: "0", label: "Sem lotação" },
                      ...lotacoes.map((lotacao) => ({
                        value: lotacao.id!.toString(),
                        label: lotacao.descricao,
                      })),
                    ]}
                  />
                </Col>
                <Col>
                  <Label htmlFor="localPagamento">Local de Pagamento</Label>
                  <SelectComponent
                    color="black"
                    placeholder="Todos locais de pagamentos"
                    isPlaceHolder={false}
                    id="localPagamento"
                    width={Number("100%")}
                    name="localPagamento"
                    disabled={isRequest}
                    value={selectLocalPagamento?.id?.toString() ?? ""}
                    onChange={(e) => {
                      const selectedLocalPagamento = locaisPagamento.find(
                        (local) => local.id === parseInt(e.target.value)
                      );
                      setSelectLocalPagamento(selectedLocalPagamento);
                      clearError(e);
                    }}
                    options={locaisPagamento.map((local) => ({
                      value: local.id!.toString(),
                      label: local.descricao,
                    }))}
                  />
                </Col>
                <Col>
                  <Label className="filtro-label" htmlFor="dataVencimento">
                    Período de pagamento <TextError>*</TextError>
                  </Label>
                  <DatePickerInputContainer
                    width={Number("100%")}
                    hasIconLeft={false}
                    hasIconRight={false}
                    error={
                      invalidFields.includes("dataVencimento") ? true : false
                    }
                    className={
                      dateRange[0] || dateRange[1] ? "has-date-content" : ""
                    }
                  >
                    <DatePicker
                      selectsRange={true}
                      name="dataVencimento"
                      startDate={dateRange[0] as Date | undefined}
                      endDate={dateRange[1] as Date | undefined}
                      onChange={(dates) => {
                        handleDateRangeChange(
                          dates as [Date | null, Date | null]
                        );

                        setInvalidFields((prev) =>
                          prev.filter((field) => field !== "dataVencimento")
                        );
                      }}
                      onChangeRaw={handleDateInputChange}
                      calendarStartDay={0}
                      className={`date-picker-input placeholder-custom ${
                        dateRange[0] || dateRange[1] ? "has-value" : "no-value"
                      }`}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="00/00/0000 - 00/00/0000"
                      showPopperArrow={false}
                      popperPlacement="bottom-start"
                      isClearable={false}
                      showYearDropdown
                      showMonthDropdown
                      scrollableMonthYearDropdown
                      wrapperClassName={
                        dateRange[0] || dateRange[1]
                          ? "date-wrapper-filled"
                          : "date-wrapper-empty"
                      }
                      locale="pt-BR"
                      autoComplete="off"
                      allowSameDay
                      onKeyDown={(e) => {
                        if (e.key === "Delete" || e.key === "Backspace") {
                          setDateRange([undefined, undefined]);
                          return;
                        }
                      }}
                    />
                  </DatePickerInputContainer>
                  {invalidFields.includes("dataVencimento") && (
                    <TextError>Selecione um período corretamente</TextError>
                  )}
                </Col>
              </ModalBody>
              <ModalFooter>
                <Button
                  schemaButton="full-primary"
                  action={() => {
                    generateReport();
                  }}
                  disabled={isRequest}
                >
                  Gerar Relatório
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalContainer>
        </Container>
      ) : (
        <LoadingOverlay>
          <LoadingGif src={loadSCGif} alt="Carregando..." />
        </LoadingOverlay>
      )}
    </>
  );
};

export default RelatorioBoletosPagosCaixa;
